import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from "vue";
  import { useToast } from "vue-toastification";
  import store from "@/store";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import {
    HouseDesignInterface,
    HouseLotsBuilderPagination,
    HouseWithLotsAndBuilder,
  } from "@/types/HouseDesignInterface";
  import { HouseFilters } from "@/types/HouseFilterInterface";
  import {
    BuildBudgetPriceEnum,
    BuildBudgetPriceParseEnum,
  } from "@/enums/BuilderBuildBudgetEnum";
  import { BorrowCalculatorResult } from "@/types/BorrowCalculator";
  import { LotInterface } from "@/types/LotInterface";
  import router from "@/router";
  import { BuilderQuote } from "@/types/BuilderQuote";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { HouseFiltersParsedFields } from "@/types/HouseFilterFieldsInterface";

  const smallLayoutWidth = 990;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'FilterHouses',
  props: {
    houseDetailsView: { type: Boolean },
    isDesignModal: { type: Boolean },
    result: {},
    lotSelected: {},
    quote: {},
    hasToLoadNextBatch: { type: Boolean }
  },
  emits: ["housesByFilter", "updateHousesPagination"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const { isDesktop } = useIsDesktop(smallLayoutWidth);

  const emits = __emit;

  const houseSelected = router.currentRoute.value.params.houseId;
  const toast = useToast();
  const houses = ref<HouseWithLotsAndBuilder[]>();
  const housesPagination = ref<HouseLotsBuilderPagination>({
    data: [],
    page: 0,
    hasNextPage: false,
    limit: 0,
    totalPages: 0,
  });
  const dataForTheField = ref<HouseFilters>();
  const isOpenHelpModal = ref(false);
  const loading = ref(false);
  const initialBudgetValue = ref<BuildBudgetPriceEnum>(
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_ANY
  );

  const filtersGlobal = computed(
    () => store.getters["borrowCalculator/getFilterFields"]
  );

  const LoanAmountString = computed(() => {
    return props.result?.LoanAmountString;
  });

  const lowerBudgetLimit = computed(() => {
    if (props.result && props.result.LoanAmountString) {
      const loanAmount = parseFloat(
        props.result.LoanAmountString.replace(/[$,]/g, "")
      );
      const sixtyPercent = Math.round((loanAmount * 0.6) / 5000) * 5000;

      getInitialBudgetValue(sixtyPercent.toString());
      return new Intl.NumberFormat("en-US").format(sixtyPercent);
    } else {
      return "";
    }
  });

  const upperBudgetLimit = computed(() => {
    if (props.result && props.result.LoanAmountString) {
      const loanAmount = parseFloat(
        props.result.LoanAmountString.replace(/[$,]/g, "")
      );
      const seventyPercent = Math.round((loanAmount * 0.7) / 5000) * 5000;

      return new Intl.NumberFormat("en-US").format(seventyPercent);
    } else {
      return "";
    }
  });

  const getInitialBudgetValue = (value: string) => {
    const lowerLimit = parseFloat(value.replace(/[$,]/g, ""));

    switch (true) {
      case lowerLimit <= 200000:
        filters.budget = BuildBudgetPriceEnum.BUDGET_THRESHOLD_200K;
        break;
      case lowerLimit <= 300000:
        filters.budget = BuildBudgetPriceEnum.BUDGET_THRESHOLD_300K;
        break;
      case lowerLimit <= 400000:
        filters.budget = BuildBudgetPriceEnum.BUDGET_THRESHOLD_400K;
        break;
      case lowerLimit <= 500000:
        filters.budget = BuildBudgetPriceEnum.BUDGET_THRESHOLD_500K;
        break;
      default:
        filters.budget = BuildBudgetPriceEnum.BUDGET_THRESHOLD_ANY;
        break;
    }
  };

  const optionsBedrooms = computed(() => {
    return dataForTheField.value ? dataForTheField.value.bedrooms : [];
  });

  const optionsBathrooms = computed(() => {
    return dataForTheField.value ? dataForTheField.value.bathrooms : [];
  });

  const optionsStoreys = computed(() => {
    return dataForTheField.value ? dataForTheField.value.storeys : [];
  });

  const filterFields = {
    budget: initialBudgetValue.value,
    bedrooms: "",
    bathrooms: "",
    storeys: "",
    lotId: props.lotSelected?._id ?? props.quote?.selections?.lot?.lotId,
  };

  const filters = reactive(Object.assign({}, filtersGlobal.value));

  const optionsBuildBudgetPrice = [
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_200K,
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_300K,
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_400K,
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_500K,
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_ANY,
  ];

  const openHelpModal = () => {
    isOpenHelpModal.value = !isOpenHelpModal.value;
  };

  const selectBudget = (budget: BuildBudgetPriceEnum) => {
    filters.budget = budget;
    store.commit("borrowCalculator/updateFilter", { budget });
  };

  const selectBedrooms = (bedrooms: string) => {
    filters.bedrooms = bedrooms;
    store.commit("borrowCalculator/updateFilter", { bedrooms });
  };

  const selectBathrooms = (bathrooms: string) => {
    filters.bathrooms = bathrooms;
    store.commit("borrowCalculator/updateFilter", { bathrooms });
  };

  const selectStoreys = (storeys: string) => {
    filters.storeys = storeys;
    store.commit("borrowCalculator/updateFilter", { storeys });
  };

  const onHowMuchYouCouldBorrow = async () => {
    await router.push({
      name: "BuilderBorrowCalculator",
      params: { estateId: props.quote?.estateId, quoteId: props.quote?._id },
    });
  };

  const getQuantitysBedroomsBathroomsStoreys = () => {
    loading.value = true;
    store
      .dispatch("builder/getQuantitysBedroomsBathroomsStoreys")
      .then((res) => {
        dataForTheField.value = res.data;
      })
      .then(() => {
        getHousesByFilter();
      })
      .catch(() => {
        toast.error("Error fetching data for the field");
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getHousesByFilter = () => {
    loading.value = true;

    if (filters && props.quote?.estateId) {
      store
        .dispatch(
          "builder/getHousesByFilter",
          createBodyRequest(props.quote?.estateId)
        )
        .then((res) => {
          updateHousesValues(res.data);
        })
        .catch((error) => {
          toast.error("Error filtering houses");
        })
        .finally(() => {
          loading.value = false;
        });
    }
  };

  const updateHousesValues = (data: HouseLotsBuilderPagination) => {
    let filteredHouses = data?.data;

    if (filteredHouses && houseSelected) {
      const excludedHouseId = houseSelected;

      houses.value = filteredHouses.filter(
        (house: HouseDesignInterface) => house._id !== excludedHouseId
      );
      housesPagination.value = data;

      emits("housesByFilter", houses.value);
      emits("updateHousesPagination", housesPagination.value);
    }
  };

  const createBodyRequest = (
    estateId: string,
    page?: number
  ): { estateId: string; query: HouseFiltersParsedFields } => {
    return {
      estateId,
      query: {
        budget: parseBudget(filtersGlobal.value.budget),
        bedrooms: parseQuantities(filtersGlobal.value.bedrooms),
        bathrooms: parseQuantities(filtersGlobal.value.bathrooms),
        storeys: parseStoreys(filtersGlobal.value.storeys),
        lotId: props.houseDetailsView ? null : filtersGlobal.value.lotId,
        page: page,
      },
    };
  };

  const parseBudget = (value: BuildBudgetPriceEnum): number | undefined => {
    switch (value) {
      case BuildBudgetPriceEnum.BUDGET_THRESHOLD_200K:
        return BuildBudgetPriceParseEnum.BUDGET_THRESHOLD_200K;
      case BuildBudgetPriceEnum.BUDGET_THRESHOLD_300K:
        return BuildBudgetPriceParseEnum.BUDGET_THRESHOLD_300K;
      case BuildBudgetPriceEnum.BUDGET_THRESHOLD_400K:
        return BuildBudgetPriceParseEnum.BUDGET_THRESHOLD_400K;
      case BuildBudgetPriceEnum.BUDGET_THRESHOLD_500K:
        return BuildBudgetPriceParseEnum.BUDGET_THRESHOLD_500K;
      default:
        return undefined;
    }
  };

  const parseQuantities = (value: string): number | undefined => {
    if (value.toLowerCase() === "any") {
      return undefined;
    }

    return value.replace(/\D/g, "") as unknown as number;
  };

  const parseStoreys = (value: string): 1 | 2 | undefined => {
    switch (value) {
      case "Single":
        return 1;
      case "Double":
        return 2;
      default:
        return undefined;
    }
  };

  const loadNextBatch = () => {
    if (props.quote?.estateId) {
      loading.value = true;

      if (filters && props.quote?.estateId) {
        store
          .dispatch(
            "builder/getHousesByFilter",
            createBodyRequest(
              props.quote?.estateId,
              housesPagination.value.page + 1
            )
          )
          .then((res) => {
            housesPagination.value = res.data;
            emits("updateHousesPagination", res.data);
          })
          .catch((error) => {
            toast.error("Error filtering houses");
          })
          .finally(() => {
            loading.value = false;
          });
      }
    }
  };

  onMounted(() => {
    getQuantitysBedroomsBathroomsStoreys();
  });

  watch(
    () => props.quote,
    () => {
      if (props.quote?.estateId && !isDesktop.value) {
        getQuantitysBedroomsBathroomsStoreys();
      }
    }
  );

  watch(
    () => props.hasToLoadNextBatch,
    () => {
      if (props.hasToLoadNextBatch) {
        loadNextBatch();
      }
    }
  );

  watch(
    filters,
    () => {
      getHousesByFilter();
    },
    { deep: true }
  );

const __returned__ = { props, smallLayoutWidth, isDesktop, emits, houseSelected, toast, houses, housesPagination, dataForTheField, isOpenHelpModal, loading, initialBudgetValue, filtersGlobal, LoanAmountString, lowerBudgetLimit, upperBudgetLimit, getInitialBudgetValue, optionsBedrooms, optionsBathrooms, optionsStoreys, filterFields, filters, optionsBuildBudgetPrice, openHelpModal, selectBudget, selectBedrooms, selectBathrooms, selectStoreys, onHowMuchYouCouldBorrow, getQuantitysBedroomsBathroomsStoreys, getHousesByFilter, updateHousesValues, createBodyRequest, parseBudget, parseQuantities, parseStoreys, loadNextBatch, computed, onMounted, reactive, ref, watch, get useToast() { return useToast }, get store() { return store }, DropDown, get HouseDesignInterface() { return HouseDesignInterface }, get HouseLotsBuilderPagination() { return HouseLotsBuilderPagination }, get HouseWithLotsAndBuilder() { return HouseWithLotsAndBuilder }, get HouseFilters() { return HouseFilters }, get BuildBudgetPriceEnum() { return BuildBudgetPriceEnum }, get BuildBudgetPriceParseEnum() { return BuildBudgetPriceParseEnum }, get BorrowCalculatorResult() { return BorrowCalculatorResult }, get LotInterface() { return LotInterface }, get router() { return router }, get BuilderQuote() { return BuilderQuote }, get useIsDesktop() { return useIsDesktop }, get HouseFiltersParsedFields() { return HouseFiltersParsedFields } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})